import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";
import { Panel } from "@/components/rights/RightsBadges";
import { SystemRow } from "@/components/rights/SystemRow";

const AddDialog = ({ open, onOpenChange, r, onDone }) => {
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const submit = async () => {
    try {
      await api.post(`/rights/${r.id}/systems`, { system_name: name, notes });
      toast.success(`${name} added`);
      setName(""); setNotes(""); onOpenChange(false); onDone();
    } catch (e) { toast.error(apiError(e, "Could not add system")); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="system-add-dialog">
        <DialogHeader>
          <DialogTitle>Add a system manually</DialogTitle>
          <DialogDescription>Use this for systems that aren't recorded in the RoPA yet.</DialogDescription>
        </DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="System name" data-testid="system-add-name" />
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" data-testid="system-add-notes" />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!name.trim()} onClick={submit} data-testid="system-add-submit">Add system</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RemoveDialog = ({ system, onClose, r, onDone }) => {
  const [reason, setReason] = useState("");
  const submit = async () => {
    try {
      await api.delete(`/rights/${r.id}/systems/${system.id}`, { data: { reason } });
      toast.success(`${system.system_name} removed`);
      setReason(""); onClose(); onDone();
    } catch (e) { toast.error(apiError(e, "Could not remove system")); }
  };
  return (
    <Dialog open={!!system} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="system-remove-dialog">
        <DialogHeader>
          <DialogTitle>Remove {system?.system_name}?</DialogTitle>
          <DialogDescription>Systems mapped from the RoPA won't be added back when you re-resolve. A reason is required.</DialogDescription>
        </DialogHeader>
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" data-testid="system-remove-reason" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" disabled={!reason.trim()} onClick={submit} data-testid="system-remove-submit">Remove</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SystemsPanel = ({ r, meta, users, canEdit, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const editable = canEdit && r.status !== "CLOSED";
  const counts = r.systems.reduce((a, s) => ({ ...a, [s.confirmation]: (a[s.confirmation] || 0) + 1 }), {});

  const resolve = async () => {
    try {
      const { data } = await api.post(`/rights/${r.id}/systems/resolve`);
      toast.success(data.added ? `${data.added} new system(s) mapped from RoPA` : "RoPA map is up to date");
      onChanged();
    } catch (e) { toast.error(apiError(e, "Could not resolve")); }
  };

  return (
    <Panel eyebrow={`Person key ${r.linked_person_key} · RoPA category "${meta.ropa_category_map[r.subject_type]}"`}
           title="Where this person's data exists" testId="systems-panel"
           actions={editable && <>
             <Button size="sm" variant="outline" onClick={resolve} data-testid="systems-resolve-button"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Re-resolve from RoPA</Button>
             <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="systems-add-button"><Plus className="w-3.5 h-3.5 mr-1" /> Add system</Button>
           </>}>
      <div className="flex gap-4 text-[11px] text-slate-600 mb-2" data-testid="systems-summary">
        <span><b className="font-mono text-[#003366]">{r.systems.length}</b> mapped</span>
        <span><b className="font-mono text-amber-600">{counts.PENDING || 0}</b> awaiting owner</span>
        <span><b className="font-mono text-[#008080]">{counts.DATA_FOUND || 0}</b> data found</span>
        <span><b className="font-mono text-slate-500">{counts.NO_DATA || 0}</b> no data</span>
      </div>
      {r.systems.length === 0 ? (
        <div className="text-xs text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-lg" data-testid="systems-empty">
          No RoPA activities cover this subject type. Add systems manually if needed.
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {r.systems.map((s) => (
            <SystemRow key={`${s.id}-${s.decision}-${s.retain_reason}`} r={r} s={s} meta={meta} users={users} canEdit={canEdit}
                       onChanged={onChanged} onRemove={setRemoving} />
          ))}
        </div>
      )}
      <AddDialog open={adding} onOpenChange={setAdding} r={r} onDone={onChanged} />
      <RemoveDialog system={removing} onClose={() => setRemoving(null)} r={r} onDone={onChanged} />
    </Panel>
  );
};
